import React, { useState, useRef, useEffect } from 'react';
import { Trash2, X } from 'lucide-react';
import { ReactSortable } from 'react-sortablejs';
import { useHistory, useLocation } from 'react-router-dom';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import { Routes } from '../../routes';
import apiHelper from '../../apiHelper';

const FORMULA_TYPES = ['Flat Amount', '% Base Price'];

let _rowId = 0;
const makeRow = (platform_fee_name = '', formula_type = '% Base Price') => ({
    id: ++_rowId,
    platform_fee_name,
    formula_type,
    value: '',
    cap: '',
});

const numericFilter = val => {
    const stripped = val.replace(/[^0-9.]/g, '');
    const parts = stripped.split('.');
    return parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : stripped;
};

// For % Base Price rate: cap at 5 decimal places and strip trailing zeros on blur
const fmtRate = val => {
    if (!val || isNaN(parseFloat(val))) return val;
    const dotIdx = val.indexOf('.');
    const decimals = dotIdx === -1 ? 0 : val.length - dotIdx - 1;
    const capped = decimals > 5 ? parseFloat(val).toFixed(5) : val;
    return String(parseFloat(capped));
};

const SgdInput = ({ value, onValueChange, onBlur, error }) => (
    <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${error ? '#DC3545' : '#6C757D'}`, borderRadius: 8, overflow: 'hidden', height: 38, background: '#fff' }}>
        <span style={{ padding: '0 12px', color: '#212529', fontSize: 14, borderRight: '1px solid #6C757D', background: '#F8F9FA', height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', flexShrink: 0 }}>
            SGD
        </span>
        <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={e => onValueChange(numericFilter(e.target.value))}
            onBlur={onBlur}
            style={{ border: 'none', outline: 'none', flex: 1, padding: '0 12px', fontSize: 14, height: '100%', background: 'transparent', minWidth: 0, fontFamily: 'Montserrat' }}
        />
    </div>
);

export default function AddPricingTemplate() {
    const history = useHistory();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isEdit = searchParams.has('edit');
    const editId = searchParams.get('edit');
    const pageTitle = isEdit ? 'Edit Pricing Template' : 'Add Pricing Template';
    const breadcrumbs = ['Settings', 'Pricing Template', pageTitle];

    const [formData, setFormData] = useState({ name: '', status: 'active', notes: '' });
    const [rows, setRows] = useState(isEdit ? [makeRow()] : [
        makeRow('Transaction Fee', '% Base Price'),
        makeRow('Commission Fee', '% Base Price'),
        makeRow('Others', 'Flat Amount'),
    ]);
    const [pageLoading, setPageLoading] = useState(isEdit);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [addFeeOpen, setAddFeeOpen] = useState(false);
    const addFeeRef = useRef(null);

    const [testCalcOpen, setTestCalcOpen] = useState(false);
    const [testBasePrice, setTestBasePrice] = useState('');

    const calcPlatformPrice = () => {
        if (!testBasePrice.trim()) return '0.00';
        const base = parseFloat(testBasePrice) || 0;
        let total = base;
        rows.forEach(row => {
            if (row.formula_type === 'Flat Amount') {
                total += parseFloat(row.value) || 0;
            } else {
                const pct = parseFloat(row.value) || 0;
                let fee = base * (pct / 100);
                const cap = parseFloat(row.cap);
                if (!isNaN(cap) && cap > 0) fee = Math.min(fee, cap);
                total += fee;
            }
        });
        return total.toFixed(2);
    };

    // Validates all rows, updates errors state, returns the error map
    const validateRowsAndUpdate = (rowList = rows) => {
        const rowErrs = {};
        const seenNames = {};

        rowList.forEach(row => {
            const name = row.platform_fee_name.trim();
            if (!name) {
                rowErrs[`row_${row.id}_platform_fee`] = 'Platform Fee is required';
            } else {
                const key = name.toLowerCase();
                if (seenNames[key] !== undefined) {
                    rowErrs[`row_${row.id}_platform_fee`] = 'Platform Fee already exists';
                    rowErrs[`row_${seenNames[key]}_platform_fee`] = 'Platform Fee already exists';
                } else {
                    seenNames[key] = row.id;
                }
            }
            if (!row.value) {
                rowErrs[`row_${row.id}_value`] = row.formula_type === 'Flat Amount' ? 'Price is required' : 'Rate is required';
            }
        });

        setErrors(prev => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => { if (!k.startsWith('row_')) next[k] = v; });
            return { ...next, ...rowErrs };
        });

        return rowErrs;
    };

    useEffect(() => {
        const handler = e => {
            if (addFeeRef.current && !addFeeRef.current.contains(e.target)) setAddFeeOpen(false);
        };
        if (addFeeOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [addFeeOpen]);

    useEffect(() => {
        if (!isEdit || !editId) return;
        const fetchTemplate = async () => {
            try {
                const res = await apiHelper(`sales/pricing-templates/${editId}/`, 'GET');
                const json = await res.json();
                if (!res.ok) { showAlert('danger', json.error || 'Failed to load template.'); setPageLoading(false); return; }
                setFormData({
                    name: json.template_name || '',
                    status: json.status || 'active',
                    notes: json.notes || '',
                });
                const feeSrc = json.fees || json.rows || [];
                if (feeSrc.length > 0) {
                    setRows(feeSrc.map(f => {
                        const formula_type = f.fee_type === 1 ? 'Flat Amount' : f.fee_type === 2 ? '% Base Price' : (f.formula_type || '% Base Price');
                        const rawValue = f.fee_value !== undefined ? String(f.fee_value) : String(f.value || '');
                        const rawCap   = f.cap_amount !== undefined ? (f.cap_amount ? String(f.cap_amount) : '') : String(f.cap || '');
                        const fmtValue = formula_type === '% Base Price'
                            ? fmtRate(rawValue)
                            : (rawValue && !isNaN(parseFloat(rawValue)) ? parseFloat(rawValue).toFixed(2) : rawValue);
                        const fmtCap   = rawCap   && !isNaN(parseFloat(rawCap))   ? parseFloat(rawCap).toFixed(2)   : rawCap;
                        return { id: ++_rowId, platform_fee_name: f.platform_fee_name || '', formula_type, value: fmtValue, cap: fmtCap };
                    }));
                }
            } catch {
                showAlert('danger', 'Failed to load template.');
            } finally {
                setPageLoading(false);
            }
        };
        fetchTemplate();
    }, []);

    const showAlert = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setIsDirty(true);
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleRowChange = (id, field, value) => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
        setIsDirty(true);
        const errKey = field === 'platform_fee_name' ? `row_${id}_platform_fee` : `row_${id}_${field}`;
        if (errors[errKey]) setErrors(prev => ({ ...prev, [errKey]: '' }));
    };

    const addRow = formula_type => {
        setRows(prev => [...prev, makeRow('', formula_type)]);
        setIsDirty(true);
    };

    const removeRow = id => {
        setRows(prev => prev.filter(r => r.id !== id));
        setIsDirty(true);
    };

    const touchAllRows = (rowList = rows) => {
        const allTouched = {};
        rowList.forEach(row => {
            allTouched[`row_${row.id}_platform_fee`] = true;
            allTouched[`row_${row.id}_value`] = true;
        });
        setTouched(prev => ({ ...prev, ...allTouched }));
    };

    const checkDuplicateName = async (nameValue) => {
        const trimmed = nameValue.trim();
        if (!trimmed) return false;
        try {
            const params = new URLSearchParams({ search: trimmed, page_size: 200 });
            const checkRes = await apiHelper(`sales/pricing-templates/?${params}`, 'GET');
            if (checkRes.ok) {
                const checkJson = await checkRes.json();
                const lowerName = trimmed.toLowerCase();
                const isDupe = (checkJson.results || []).some(t => {
                    if (t.archived_at || t.archived_by || t.archived_by_full_name || t.archived_by_name) return false;
                    const tName = (t.template_name || '').toLowerCase().trim();
                    if (tName !== lowerName) return false;
                    if (isEdit && String(t.pricing_template_id) === String(editId)) return false;
                    return true;
                });
                if (isDupe) {
                    setErrors(prev => ({ ...prev, name: 'Template Name already exists' }));
                    return true;
                }
            }
        } catch { /* ignore */ }
        return false;
    };

    const handleSave = async () => {
        touchAllRows();
        const formErrs = {};
        if (!formData.name.trim()) formErrs.name = 'Template Name is required';
        setErrors(prev => ({ ...prev, ...formErrs }));

        const rowErrs = validateRowsAndUpdate();

        if (Object.keys(formErrs).length > 0 || Object.keys(rowErrs).length > 0) {
            showAlert('danger', 'Error Saving');
            return;
        }

        // Pre-check: only validate name uniqueness against non-archived templates
        try {
            const isDupe = await checkDuplicateName(formData.name);
            if (isDupe) {
                showAlert('danger', 'Error Saving');
                return;
            }
        } catch { /* proceed to save */ }

        try {
            const payload = {
                template_name: formData.name.trim(),
                status: formData.status,
                notes: formData.notes.trim(),
                rows: rows.map(r => ({
                    platform_fee_name: r.platform_fee_name.trim(),
                    formula_type: r.formula_type,
                    value: r.value,
                    cap: r.cap,
                })),
            };

            const endpoint = isEdit
                ? `sales/pricing-templates/update/${editId}/`
                : 'sales/pricing-templates/create/';
            const res = await apiHelper(endpoint, 'POST', payload);
            const json = await res.json();

            if (!res.ok) {
                const isDuplicateName =
                    (json.template_name && Array.isArray(json.template_name)) ||
                    (typeof json.error === 'string' && /exists|unique|duplicate/i.test(json.error)) ||
                    (typeof json.detail === 'string' && /exists|unique|duplicate/i.test(json.detail));
                if (isDuplicateName) {
                    // Only show inline error if the conflict is with a non-archived template
                    let conflictsWithNonArchived = false;
                    try {
                        const params2 = new URLSearchParams({ search: formData.name.trim(), page_size: 200 });
                        const checkRes2 = await apiHelper(`sales/pricing-templates/?${params2}`, 'GET');
                        if (checkRes2.ok) {
                            const checkJson2 = await checkRes2.json();
                            const lowerName2 = formData.name.trim().toLowerCase();
                            conflictsWithNonArchived = (checkJson2.results || []).some(t => {
                                if (t.archived_at || t.archived_by || t.archived_by_full_name || t.archived_by_name) return false;
                                const tName = (t.template_name || '').toLowerCase().trim();
                                if (tName !== lowerName2) return false;
                                if (isEdit && String(t.pricing_template_id) === String(editId)) return false;
                                return true;
                            });
                        }
                    } catch { conflictsWithNonArchived = true; }
                    if (conflictsWithNonArchived) {
                        setErrors(prev => ({ ...prev, name: 'Template Name already exists' }));
                    }
                    showAlert('danger', 'Error Saving');
                } else {
                    showAlert('danger', json.error || 'Error saving. Please try again.');
                }
                return;
            }

            setIsDiscarding(true);
            setIsDirty(false);
            setTimeout(() => history.push(Routes.PricingTemplate.path, { success: isEdit ? 'Pricing Template Updated Successfully' : 'Pricing Template Saved Successfully' }), 100);
        } catch (err) {
            showAlert('danger', 'Error saving. Please try again.');
        }
    };

    const handleDiscardConfirm = confirm => {
        if (confirm) {
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);
            setTimeout(() => history.push(Routes.PricingTemplate.path, { success: 'Pricing Template Discarded Successfully' }), 100);
        } else {
            setDiscardModalOpen(false);
        }
    };

    const basePriceChipStyle = {
        display: 'inline-flex', alignItems: 'center', gap: 4,
        color: '#0D6EFD', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
    };

    if (pageLoading) {
        return (
            <div className="company-body main-company-body-inner new-look" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <span style={{ fontFamily: 'Montserrat', color: '#6C757D', fontSize: 14 }}>Loading...</span>
            </div>
        );
    }

    return (
        <div className="company-body main-company-body-inner new-look" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {alert.show && (
                <AlertNotification type={alert.type} message={alert.message} onClose={() => setAlert({ show: false, type: '', message: '' })} />
            )}

            {/* Breadcrumb */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, idx) => (
                    <span
                        key={idx}
                        className={`breadcrumb-item ${crumb === 'Pricing Template' ? 'clickable' : ''}`}
                        onClick={() => crumb === 'Pricing Template' && history.push(Routes.PricingTemplate.path)}
                        style={{ cursor: crumb === 'Pricing Template' ? 'pointer' : 'default' }}
                    >
                        {crumb}{idx < breadcrumbs.length - 1 ? ' ' : ''}
                    </span>
                ))}
            </div>

            {/* Title + action buttons */}
            <div className="edit-organisation-info">
                <div className="edit-organisation-left">
                    <h1>{pageTitle}</h1>
                </div>
                <div className="discard-save-organisation-right d-flex align-items-center gap-2">
                    <button
                        className="dark-button"
                        type="button"
                        style={{ display: 'flex', height: 34, minWidth: 100, padding: '8px', justifyContent: 'center', alignItems: 'center', gap: 10, borderRadius: 8 }}
                        onClick={() => setDiscardModalOpen(true)}
                    >
                        Discard
                    </button>
                    <DiscardConfirmationModal
                        isOpen={discardModalOpen}
                        onCancel={() => setDiscardModalOpen(false)}
                        onConfirm={handleDiscardConfirm}
                        entityName="Pricing Template"
                    />
                    <button
                        className="dark-border"
                        type="button"
                        style={{ display: 'flex', height: 34, minWidth: 100, padding: '8px', justifyContent: 'center', alignItems: 'center', gap: 10, borderRadius: 8, boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' }}
                        onClick={handleSave}
                    >
                        Save
                    </button>
                </div>
            </div>

            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 24 }}>
                <div className="info-section">

                    {/* ── Basic Info ── */}
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header">
                            <h4 className="info-title">Basic Info</h4>
                        </div>
                        <div className="info-content d-block">
                            <div className="grid-info-adding">
                                <div className="row gappingcls-added">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label className="form-label d-flex align-items-center gap-1">
                                                Template Name <span className="error-color">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control common-input-all"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                onBlur={() => checkDuplicateName(formData.name)}
                                                style={errors.name ? { borderColor: '#dc3545' } : { border: '1px solid #6C757D' }}
                                            />
                                            {errors.name && (
                                                <div style={{ fontSize: 12, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 5 }}>
                                                    {errors.name}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <div className="info-inner">
                                            <label className="form-label">
                                                Status <span className="error-color">*</span>
                                            </label>
                                            <div className="flexing-type-radio">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="status" id="statusActive" value="active" checked={formData.status === 'active'} onChange={handleChange} />
                                                    <label className="form-check-label" htmlFor="statusActive" style={{ fontWeight: 'normal', fontSize: 14 }}>Active</label>
                                                </div>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="status" id="statusInactive" value="inactive" checked={formData.status === 'inactive'} onChange={handleChange} />
                                                    <label className="form-check-label" htmlFor="statusInactive" style={{ fontWeight: 'normal', fontSize: 14 }}>Inactive</label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Formula Info ── */}
                    <div className="info-card new-info-overflow-auto">
                        <div className="info-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 className="info-title">Formula Info</h4>
                            <button
                                type="button"
                                style={{ height: 34, padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', background: '#fff', border: '1px solid #212529', color: '#212529', cursor: 'pointer' }}
                                onClick={() => {
                                    touchAllRows();
                                    const rowErrs = validateRowsAndUpdate();
                                    if (Object.keys(rowErrs).length > 0) return;
                                    setTestBasePrice('');
                                    setTestCalcOpen(true);
                                }}
                            >
                                Test Calculation
                            </button>
                        </div>
                        <div className="info-content d-block">

                            {/* Formula description */}
                            <div style={{ marginBottom: 20, fontFamily: 'Montserrat', fontSize: 13 }}>
                                <p style={{ color: '#212529', marginBottom: 16, lineHeight: '1.6' }}>
                                    <span style={{ fontWeight: 600 }}>Formula Purpose:</span>{'  '}The formula will calculate Platform Price based on Base Price when initiate Platform Pricing.
                                </p>
                                <p style={{ color: '#5E6E82', marginBottom: 16, padding: '10px 16px', background: '#F5F6F8', borderRadius: 6, lineHeight: '1.6' }}>
                                    Platform Price = Base Price + Platform Fees
                                </p>
                                <p style={{ color: '#212529', marginBottom: 16, lineHeight: '1.6' }}>
                                    <span style={{ fontWeight: 600 }}>Assume:</span>{'  '}"Base Price = SGD 100.00;{'  '}Platform Fee 1 = Transaction Fee (Base Price × 5%);{'  '}Platform Fee 2 = Others (SGD 10.00)"
                                </p>
                                <p style={{ color: '#5E6E82', marginBottom: 0, padding: '10px 16px', background: '#F5F6F8', borderRadius: 6, lineHeight: '1.6' }}>
                                    Platform Price = SGD 100.00 + (SGD 100.00 × 5%) + SGD 10.00 = SGD 115.00
                                </p>
                            </div>

                            {/* Fee table */}
                            <div style={{ borderBottom: '1px solid #DEE2E6', marginBottom: 12 }}>

                                {/* Header */}
                                <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 1fr 1fr 44px', background: '#E9ECEF', padding: '0 12px', alignItems: 'center', height: 44, gap: 12, borderBottom: '1px solid #E9ECEF' }}>
                                    <div />
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat' }}>
                                        Platform Fee <span style={{ color: '#DC3545' }}>*</span>
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat' }}>
                                        Value <span style={{ color: '#DC3545' }}>*</span>
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat' }}>Cap (Max)</div>
                                    <div />
                                </div>

                                {/* Rows */}
                                <ReactSortable
                                    list={rows}
                                    setList={setRows}
                                    handle=".drag-handle"
                                    animation={150}
                                    style={{ display: 'contents' }}
                                >
                                {rows.map((row, idx) => (
                                    <div
                                        key={row.id}
                                        style={{ display: 'grid', gridTemplateColumns: '44px 1fr 1fr 1fr 44px', padding: '10px 12px', borderBottom: '1px solid #E9ECEF', alignItems: 'start', gap: 12, background: idx % 2 === 0 ? '#fff' : '#F8F9FA' }}
                                    >
                                        {/* Trash — first row disabled */}
                                        <button
                                            type="button"
                                            disabled={idx === 0}
                                            onClick={() => removeRow(row.id)}
                                            style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'not-allowed' : 'pointer', color: idx === 0 ? '#CED4DA' : '#6C757D', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 4, marginTop: 7 }}
                                        >
                                            <Trash2 size={16} />
                                        </button>

                                        {/* Platform Fee — editable text */}
                                        <div>
                                            <input
                                                type="text"
                                                value={row.platform_fee_name}
                                                onChange={e => handleRowChange(row.id, 'platform_fee_name', e.target.value)}
                                                onBlur={() => { setTouched(prev => ({ ...prev, [`row_${row.id}_platform_fee`]: true })); validateRowsAndUpdate(); }}
                                                style={{
                                                    width: '100%', height: 38, padding: '0 12px', fontSize: 14,
                                                    border: `1px solid ${touched[`row_${row.id}_platform_fee`] && errors[`row_${row.id}_platform_fee`] ? '#DC3545' : '#212529'}`,
                                                    borderRadius: 8, outline: 'none', boxSizing: 'border-box',
                                                    fontFamily: 'Montserrat', background: '#fff',
                                                }}
                                            />
                                            {touched[`row_${row.id}_platform_fee`] && errors[`row_${row.id}_platform_fee`] && (
                                                <div style={{ color: '#DC3545', fontSize: 11, marginTop: 3, fontFamily: 'Montserrat' }}>
                                                    {errors[`row_${row.id}_platform_fee`]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Value — varies by formula_type */}
                                        <div>
                                            {row.formula_type === 'Flat Amount' ? (
                                                <>
                                                    <SgdInput
                                                        value={row.value}
                                                        onValueChange={v => handleRowChange(row.id, 'value', v)}
                                                        onBlur={() => {
                                                            if (row.value && !isNaN(parseFloat(row.value))) {
                                                                handleRowChange(row.id, 'value', parseFloat(row.value).toFixed(2));
                                                            }
                                                            setTouched(prev => ({ ...prev, [`row_${row.id}_value`]: true }));
                                                            validateRowsAndUpdate();
                                                        }}
                                                        error={touched[`row_${row.id}_value`] && errors[`row_${row.id}_value`]}
                                                    />
                                                    {touched[`row_${row.id}_value`] && errors[`row_${row.id}_value`] && (
                                                        <div style={{ color: '#DC3545', fontSize: 11, marginTop: 3, fontFamily: 'Montserrat' }}>
                                                            {errors[`row_${row.id}_value`]}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                    <div style={{ ...basePriceChipStyle, marginTop: 10 }}>
                                                        <span>&#123;Base Price&#125;</span>
                                                        <span style={{ fontSize: 15 }}>×</span>
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${touched[`row_${row.id}_value`] && errors[`row_${row.id}_value`] ? '#DC3545' : '#6C757D'}`, borderRadius: 8, overflow: 'hidden', height: 38, background: '#fff' }}>
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                value={row.value}
                                                                onChange={e => handleRowChange(row.id, 'value', numericFilter(e.target.value))}
                                                                onBlur={() => {
                                                                    handleRowChange(row.id, 'value', fmtRate(row.value));
                                                                    setTouched(prev => ({ ...prev, [`row_${row.id}_value`]: true }));
                                                                    validateRowsAndUpdate();
                                                                }}
                                                                style={{ border: 'none', outline: 'none', flex: 1, padding: '0 10px', fontSize: 14, height: '100%', background: 'transparent', minWidth: 0, fontFamily: 'Montserrat' }}
                                                            />
                                                            <span style={{ padding: '0 12px', color: '#212529', fontSize: 14, borderLeft: '1px solid #6C757D', background: '#F8F9FA', height: '100%', display: 'flex', alignItems: 'center', flexShrink: 0 }}>%</span>
                                                        </div>
                                                        {touched[`row_${row.id}_value`] && errors[`row_${row.id}_value`] && (
                                                            <div style={{ color: '#DC3545', fontSize: 11, marginTop: 3, fontFamily: 'Montserrat' }}>
                                                                {errors[`row_${row.id}_value`]}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Cap (Max) — only for % Base Price */}
                                        {row.formula_type === '% Base Price' ? (
                                            <SgdInput
                                                value={row.cap}
                                                onValueChange={v => handleRowChange(row.id, 'cap', v)}
                                                onBlur={() => {
                                                    if (row.cap && !isNaN(parseFloat(row.cap))) {
                                                        handleRowChange(row.id, 'cap', parseFloat(row.cap).toFixed(2));
                                                    }
                                                    validateRowsAndUpdate();
                                                }}
                                            />
                                        ) : <div />}

                                        {/* Drag handle */}
                                        <div className="drag-handle" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'grab', userSelect: 'none', flexShrink: 0, paddingTop: 10 }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                <path d="M3 9H15M3 13.5H15M3 4.5H15" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                ))}
                                </ReactSortable>
                            </div>

                            {/* Add Platform Fee */}
                            <div style={{ position: 'relative', display: 'inline-block' }} ref={addFeeRef}>
                                <button
                                    type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #212529', borderRadius: 8, background: '#fff', padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#212529' }}
                                    onClick={() => setAddFeeOpen(prev => !prev)}
                                >
                                    Add Platform Fee
                                    <svg width="10" height="6" viewBox="0 0 10 6" fill="#212529" xmlns="http://www.w3.org/2000/svg"
                                        style={{ transition: 'transform 0.2s', transform: addFeeOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>
                                        <path d="M0 0L5 6L10 0H0Z"/>
                                    </svg>
                                </button>
                                {addFeeOpen && (
                                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#fff', border: '1px solid #E9ECEF', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.10)', zIndex: 200, width: '100%' }}>
                                        {FORMULA_TYPES.map(type => (
                                            <button
                                                key={type}
                                                type="button"
                                                style={{ display: 'block', width: '100%', padding: '10px 16px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 400, color: '#212529' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#FFFEF0'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                                onClick={() => { addRow(type); setAddFeeOpen(false); }}
                                            >
                                                {type}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Notes ── */}
                    <div className="info-card">
                        <div className="info-header">
                            <h4 className="info-title">Notes</h4>
                        </div>
                        <div className="info-content">
                            <div className="info-inner common-textarea">
                                <textarea
                                    className="form-control w-100 common-textarea-all"
                                    rows="3"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    style={{ border: '1px solid #6C757D' }}
                                />
                            </div>
                        </div>
                        <UnsavedChangesPrompt isDirty={isDirty && !isDiscarding} entityName="Pricing Template" />
                    </div>

                </div>
            </div>

            {/* Test Calculation Modal */}
            {testCalcOpen && (
                <>
                    <div className="drawer-overlay" onClick={() => setTestCalcOpen(false)} />
                    <div style={{
                        position: 'fixed', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: '#fff', borderRadius: 12,
                        padding: '20px 25px', zIndex: 1200,
                        width: 585, maxWidth: '90vw',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 20,
                    }}>
                        <button
                            type="button"
                            onClick={() => setTestCalcOpen(false)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#212529', padding: 0, lineHeight: 0 }}
                        >
                            <X size={18} />
                        </button>

                        <div style={{ width: '100%' }}>
                            <label style={{ fontSize: 12, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat', display: 'block', marginBottom: 8 }}>
                                Base Price <span style={{ color: '#DC3545' }}>*</span>
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #6C757D', borderRadius: 8, overflow: 'hidden', height: 38, background: '#fff' }}>
                                <span style={{ padding: '0 12px', color: '#212529', fontSize: 14, borderRight: '1px solid #6C757D', background: '#F8F9FA', height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    SGD
                                </span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    autoFocus
                                    value={testBasePrice}
                                    onChange={e => setTestBasePrice(numericFilter(e.target.value))}
                                    onBlur={() => {
                                        if (testBasePrice && !isNaN(parseFloat(testBasePrice))) {
                                            setTestBasePrice(parseFloat(testBasePrice).toFixed(2));
                                        }
                                    }}
                                    style={{ border: 'none', outline: 'none', flex: 1, padding: '0 12px', fontSize: 14, height: '100%', background: 'transparent', fontFamily: 'Montserrat', minWidth: 0 }}
                                />
                            </div>
                        </div>

                        <div style={{ width: '100%' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat', marginBottom: 6 }}>
                                Platform Price
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#212529', fontFamily: 'Montserrat' }}>
                                SGD {calcPlatformPrice()}
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => setTestCalcOpen(false)}
                            style={{ display: 'flex', height: 34, minWidth: 100, padding: '0 15px', justifyContent: 'center', alignItems: 'center', gap: 10, borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: 'Montserrat', background: '#212529', color: '#fff', border: 'none', cursor: 'pointer' }}
                        >
                            Close
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}